import {latestReviewedClaim, resolveAllotmentCarryForward} from '../core/allotmentCarryForward'
import React, {FC, useState} from 'react'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {Rank, Crew, Company, CompanyAdmin, Vessel} from '../core/_models'
import {
  CrewClaim,
  CrewDeduction,
  getCrewAllotments,
  getCrewClaims,
  getCrewDeductions,
  getCrewLatestBankingDetails,
  getCrewOvertimes,
  getSignOnOffRecords,
  SignOnOffRecord,
} from '../core/_requests'
import {toSignOnMonthKey} from '../core/signOnDateUtils'
import {
  blurNumberInputOnWheel,
  preventNumberInputArrowStep,
} from '../../../shared/utils/numberInputGuards'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const MILLIS_IN_DAY = 24 * 60 * 60 * 1000

const parseIsoDateOnly = (value?: string | null): Date | null => {
  if (!value) return null
  const datePart = value.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(Date.UTC(year, month - 1, day))
}

const buildMonthRange = (claimMonth?: string | null) => {
  const monthDate = parseIsoDateOnly(claimMonth)
  if (!monthDate) return null
  const year = monthDate.getUTCFullYear()
  const month = monthDate.getUTCMonth()
  const start = new Date(Date.UTC(year, month, 1))
  const end = new Date(Date.UTC(year, month + 1, 0))
  const calendarDaysInMonth = end.getUTCDate()
  const wageCalculationDays = 30
  return {start, end, calendarDaysInMonth, wageCalculationDays}
}

const todayUtcDateOnly = (): Date => {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

const daysWorkedInMonth = (
  records: SignOnOffRecord[],
  crewId: number,
  claimMonth?: string | null
): number => {
  const monthRange = buildMonthRange(claimMonth)
  if (!monthRange || !crewId) return 0

  let totalDays = 0

  const today = todayUtcDateOnly()

  records.forEach((record) => {
    if (Number(record.crewId) !== Number(crewId)) return

    const signOn = parseIsoDateOnly(record.signOnDate)
    if (!signOn) return

    // Claim wages should never project beyond today for open/future sign-off legs.
    const signOff = parseIsoDateOnly(record.signOffDate)
    const workWindowEnd = signOff ? (signOff < today ? signOff : today) : today

    const overlapStart = signOn > monthRange.start ? signOn : monthRange.start
    const overlapEnd = workWindowEnd < monthRange.end ? workWindowEnd : monthRange.end

    if (overlapEnd < overlapStart) return

    const days = Math.floor((overlapEnd.getTime() - overlapStart.getTime()) / MILLIS_IN_DAY) + 1
    totalDays += days
  })

  const shouldNormalizeToThirtyDays =
    monthRange.calendarDaysInMonth < monthRange.wageCalculationDays &&
    totalDays >= monthRange.calendarDaysInMonth

  const normalizedDays = shouldNormalizeToThirtyDays ? monthRange.wageCalculationDays : totalDays

  return Math.max(0, Math.min(monthRange.wageCalculationDays, normalizedDays))
}

const resolveCurrencyCode = (
  salaryCurrency?: string | null,
  salaryOtherCurrency?: string | null
) => {
  const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
  return normalizedAccountCurrency
}

const normalizeMonthStart = (value?: string | null): string => {
  const raw = (value || '').trim()
  if (!raw) return ''
  const monthPart = raw.slice(0, 7)
  const [yearText, monthText] = monthPart.split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return ''
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`
}

const previousMonthStart = (monthStart: string): string => {
  const normalized = normalizeMonthStart(monthStart)
  if (!normalized) return ''
  const [yearText, monthText] = normalized.slice(0, 7).split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  const prev = new Date(Date.UTC(year, month - 2, 1))
  return `${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}-01`
}

const isAllotmentOfficeReviewed = (row: {
  workflowStatus?: string | null
  reviewedAt?: string | null
}) => row.workflowStatus === 'REVIEWED' || Boolean(row.reviewedAt)

const isOvertimeOfficeReviewed = (row: {status?: string | null; reviewedAt?: string | null}) =>
  row.status === 'REVIEWED' || Boolean(row.reviewedAt)

const resolvePreviousMonthCarryForward = (
  allotments: Array<{
    statementMonth?: string | null
    carryForwardAmount?: number | null
    balanceBroughtForward?: number | null
    allotmentId?: number | null
    workflowStatus?: string | null
    reviewedAt?: string | null
  }>,
  claimMonth?: string | null,
  claims?: Array<{
    crewId?: number | string | null
    claimMonth?: string | null
    balanceCf?: number | null
    bfAmount?: number | null
    wagesEarned?: number | null
    deductionsTotal?: number | null
    id?: number
    status?: string | null
    reviewedAt?: string | null
  }>,
  crewId?: number | string | null
): number => {
  const claimMonthStart = normalizeMonthStart(claimMonth)
  if (!claimMonthStart) return 0

  const prevMonth = previousMonthStart(claimMonthStart)
  if (!prevMonth) return 0

  const reviewed = allotments.filter((row) => isAllotmentOfficeReviewed(row))
  const pool = reviewed.length > 0 ? reviewed : allotments

  const normalized = pool
    .map((row) => {
      const rowMonthStart = normalizeMonthStart(row.statementMonth || undefined)
      const reviewedClaim = rowMonthStart && crewId != null
        ? latestReviewedClaim(claims || [], crewId, rowMonthStart.slice(0, 7)) : undefined
      const balance = resolveAllotmentCarryForward(row as any, reviewedClaim)
      return {
        id: Number(row.allotmentId) || 0,
        monthStart: rowMonthStart,
        balance,
      }
    })
    .filter((row) => row.monthStart)

  const exactPrev = normalized
    .filter((row) => row.monthStart === prevMonth)
    .sort((a, b) => b.id - a.id)[0]

  if (exactPrev) {
    return Number.isFinite(exactPrev.balance) ? exactPrev.balance : 0
  }

  const latestBefore = normalized
    .filter((row) => row.monthStart < claimMonthStart)
    .sort((a, b) => {
      if (a.monthStart === b.monthStart) return b.id - a.id
      return a.monthStart < b.monthStart ? 1 : -1
    })[0]

  return latestBefore && Number.isFinite(latestBefore.balance) ? latestBefore.balance : 0
}

const getCurrencySymbol = (currencyCode?: string) => {
  const code = (currencyCode || '').toString().trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '\u20AC'
    case 'GBP':
      return '\u00A3'
    case 'SGD':
      return 'S$'
    case 'INR':
      return '\u20B9'
    case 'AED':
      return '\u062F.\u0625 '
    default:
      try {
        const part = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
          .formatToParts(0)
          .find((p) => p.type === 'currency')
        const maybeSymbol = (part?.value || '').trim()
        return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
      } catch {
        return ''
      }
  }
}

// Type for pending file uploads (for new claims)
export interface PendingAttachments {
  files: File[]
}

interface CrewClaimModalProps {
  isOpen: boolean
  canCreate: boolean
  canEditDraft?: boolean
  canReview: boolean
  isNewClaim: boolean
  isOfficeEntry?: boolean
  showCompanyFilter?: boolean
  showSubCompanyFilter?: boolean
  lockCrewSelection?: boolean
  claim: CrewClaim | null
  ranks: Rank[]
  crewMembers: Crew[]
  companies?: Company[]
  companyAdmins?: CompanyAdmin[]
  vessels?: Vessel[]
  selectedCompanyGroupId?: number | ''
  selectedCompanyAdminId?: number | ''
  selectedVesselId?: number | ''
  onCompanyGroupChange?: (next: number | '') => void
  onCompanyAdminChange?: (next: number | '') => void
  onVesselChange?: (next: number | '') => void
  onClose: () => void
  onSave: (pendingFiles?: File[]) => void
  onChange: (nextClaim: CrewClaim) => void
  onAttachmentAdd: (files: FileList | null) => void
  onAttachmentRemove: (attachmentId: number) => void
  calculateRowTotal: (claim: CrewClaim) => number
  formatCurrency: (value: number) => string
  isLoading: boolean
  restrictToMedicalTravelOnFirstFill?: boolean
  showOneTimeClaimFields?: boolean
  lockClaimAmountInputs?: boolean
  canEditMasterPending?: boolean
  canEditSubmittedByOffice?: boolean
  lockCrewEnteredCoreFieldsForMaster?: boolean
  crewSignOnDateById?: Map<number, string>
}

const CrewClaimModal: FC<CrewClaimModalProps> = ({
  isOpen,
  canCreate,
  canEditDraft = false,
  canReview,
  isNewClaim,
  isOfficeEntry = false,
  showCompanyFilter = true,
  showSubCompanyFilter = true,
  lockCrewSelection = false,
  claim,
  ranks,
  crewMembers,
  companies,
  companyAdmins,
  vessels,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  onCompanyGroupChange,
  onCompanyAdminChange,
  onVesselChange,
  onClose,
  onSave,
  onChange,
  onAttachmentAdd,
  onAttachmentRemove,
  calculateRowTotal,
  formatCurrency,
  isLoading,
  restrictToMedicalTravelOnFirstFill = false,
  showOneTimeClaimFields = true,
  lockClaimAmountInputs = false,
  canEditMasterPending = false,
  canEditSubmittedByOffice = false,
  lockCrewEnteredCoreFieldsForMaster = false,
  crewSignOnDateById = new Map<number, string>(),
}) => {
  // State for pending file uploads (for new claims)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [selectedFileNames, setSelectedFileNames] = useState<string>('')
  const [signOnOffRecords, setSignOnOffRecords] = useState<SignOnOffRecord[]>([])
  const [crewDeductions, setCrewDeductions] = useState<CrewDeduction[]>([])
  const [showOvertimeAmountField, setShowOvertimeAmountField] = useState(false)

  const {isSuperAdmin, hasPermission, hasAnyPermission} = useAuth()
  const canAddClaimAttachment =
    isSuperAdmin || hasAnyPermission(['CREATE_CREW_CLAIM', 'UPDATE_CREW_CLAIM'])
  const canRemoveClaimAttachment = isSuperAdmin || hasPermission('DELETE_CREW_CLAIM')

  const filteredCompanyAdmins = React.useMemo(() => {
    if (!companies) return []
    if (!selectedCompanyGroupId) return companies
    return companies.filter((ca) => {
      const parentCompanyId =
        (ca as any)?.cga?.id ??
        (ca as any)?.cgaid?.id ??
        (ca as any)?.companyGroupAdmin?.id ??
        (ca as any)?.companyGroupAdminId
      return parentCompanyId === Number(selectedCompanyGroupId)
    })
  }, [companies, selectedCompanyGroupId])

  React.useEffect(() => {
    if (!selectedCompanyAdminId) return
    const exists = filteredCompanyAdmins.some((ca) => ca.id === Number(selectedCompanyAdminId))
    if (!exists) {
      onCompanyAdminChange?.('')
      onVesselChange?.('')
    }
  }, [selectedCompanyAdminId, filteredCompanyAdmins, onCompanyAdminChange, onVesselChange])

  const filteredVessels = React.useMemo(() => {
    if (!vessels) return []
    const getVesselCompanyAdminId = (v: Vessel): number | undefined =>
      v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined

    const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
      (v as any).companyGroupAdminId ??
      (v as any).companyGroupAdmin?.id ??
      (v.companyAdmin as any)?.cgaid?.id ??
      (v.companyAdmin as any)?.cga?.id

    if (selectedCompanyAdminId) {
      return vessels.filter((v) => getVesselCompanyAdminId(v) === Number(selectedCompanyAdminId))
    }
    if (selectedCompanyGroupId) {
      return vessels.filter((v) => getVesselCompanyGroupId(v) === Number(selectedCompanyGroupId))
    }
    return vessels
  }, [vessels, selectedCompanyAdminId, selectedCompanyGroupId])

  React.useEffect(() => {
    if (!isOpen) return

    getSignOnOffRecords()
      .then((records) => setSignOnOffRecords(records || []))
      .catch((error) => {
        console.error('Error fetching sign-on/off records for wage calculation:', error)
        setSignOnOffRecords([])
      })
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen) return

    getCrewDeductions()
      .then((rows) => setCrewDeductions(rows || []))
      .catch((error) => {
        console.error('Error fetching crew deductions for claim formula:', error)
        setCrewDeductions([])
      })
  }, [isOpen])

  const toAmount = (value: unknown): number => {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
  }

  const resolveOvertimeAmountForClaim = (
    overtimes: Array<{
      crewId?: number
      overtimeMonth?: string | null
      otAmount?: number
      status?: string | null
      reviewedAt?: string | null
    }>,
    crewId?: number,
    claimMonth?: string | null
  ): number | null => {
    const crew = Number(crewId || 0)
    const monthKey = normalizeMonthStart(claimMonth).slice(0, 7)
    if (!crew || !monthKey) return null

    const matches = overtimes.filter((row) => {
      const sameCrew = Number(row.crewId || 0) === crew
      const sameMonth = normalizeMonthStart(row.overtimeMonth || undefined).slice(0, 7) === monthKey
      return sameCrew && sameMonth && isOvertimeOfficeReviewed(row)
    })

    if (matches.length === 0) return null

    const toSortableTime = (value?: string | null) => {
      const timestamp = Date.parse(value || '')
      return Number.isFinite(timestamp) ? timestamp : 0
    }

    const sorted = [...matches].sort((first, second) => {
      const firstUpdated = toSortableTime((first as any).updatedAt)
      const secondUpdated = toSortableTime((second as any).updatedAt)
      if (firstUpdated !== secondUpdated) return secondUpdated - firstUpdated

      const firstCreated = toSortableTime((first as any).createdAt)
      const secondCreated = toSortableTime((second as any).createdAt)
      if (firstCreated !== secondCreated) return secondCreated - firstCreated

      return Number((second as any).id || 0) - Number((first as any).id || 0)
    })

    const readOtAmount = (row: any) => toAmount(row?.otAmount)

    // OT Amount is the source of truth; prefer latest row with a non-zero OT Amount.
    const bestNonZero = sorted.find((row: typeof matches[number]) => readOtAmount(row) > 0)
    if (bestNonZero) return readOtAmount(bestNonZero)

    // Fallback to latest matched row's OT Amount (can be zero).
    return sorted.length > 0 ? readOtAmount(sorted[0]) : null
  }

  const withDerivedValues = (nextClaim: CrewClaim): CrewClaim => {
    const crewClaimsAmount =
      toAmount(nextClaim.medical) +
      toAmount(nextClaim.safetyGear) +
      toAmount(nextClaim.travel) +
      toAmount(nextClaim.boardingLodging) +
      toAmount(nextClaim.hraAllowance) +
      toAmount(nextClaim.bonus) +
      toAmount(nextClaim.miscReimbursements) +
      toAmount(nextClaim.overtimeAmount)

    const deductionsTotal =
      toAmount(nextClaim.deductionCashAdvanceFromOffice) +
      toAmount(nextClaim.deductionOtherDeductions) +
      toAmount(nextClaim.deductionInternetCard)

    const computedBalanceCf =
      toAmount(nextClaim.bfAmount) +
      toAmount(nextClaim.wagesEarned) +
      crewClaimsAmount -
      deductionsTotal
    const balanceCf = Math.max(computedBalanceCf, 0)

    return {
      ...nextClaim,
      totalAmount: crewClaimsAmount,
      allotmentAmount: crewClaimsAmount,
      cashAdvance: deductionsTotal,
      balanceCf,
      deductionsTotal,
    }
  }

  const getCrewDeductionForClaimMonth = (crewId?: number, claimMonth?: string | null): number => {
    const id = Number(crewId || 0)
    if (!id || !claimMonth) return 0

    const monthKey = claimMonth.slice(0, 7)
    return crewDeductions
      .filter((row) => Number(row.crewId) === id && (row.claimMonth || '').slice(0, 7) === monthKey)
      .reduce((sum, row) => sum + toAmount(row.deductionsTotal), 0)
  }

  React.useEffect(() => {
    if (!claim || !isOpen) return

    const crewId = Number(claim.crewId || 0)
    if (!crewId || !claim.claimMonth) return

    let isMounted = true

    const hydrateClaimData = async () => {
      try {
        const [allotments, overtimes, claimsList] = await Promise.all([
          getCrewAllotments(crewId).catch(() => []),
          getCrewOvertimes().catch(() => []),
          getCrewClaims().catch(() => []),
        ])

        if (!isMounted) return

        // 1. Resolve B/F Amount
        const nextBfAmount = resolvePreviousMonthCarryForward(allotments, claim.claimMonth, claimsList, crewId)

        // 2. Resolve Wages Earned
        const monthRange = buildMonthRange(claim.claimMonth)
        let proratedWages = 0
        if (monthRange) {
          const workedDays = daysWorkedInMonth(signOnOffRecords, crewId, claim.claimMonth)
          const monthlySalary = toAmount(claim.salary)
          proratedWages =
            monthRange.wageCalculationDays > 0
              ? Number(((monthlySalary * workedDays) / monthRange.wageCalculationDays).toFixed(2))
              : 0
        }

        // 3. Resolve Overtime Amount
        const fetchedOvertimeAmount = resolveOvertimeAmountForClaim(
          overtimes,
          crewId,
          claim.claimMonth
        )
        const nextOvertime = fetchedOvertimeAmount ?? 0
        setShowOvertimeAmountField(fetchedOvertimeAmount != null)

        // 4. Resolve Deductions
        const nextCrewDeduction = getCrewDeductionForClaimMonth(crewId, claim.claimMonth)

        // Only call onChange if something has actually changed
        if (
          toAmount(claim.bfAmount) === nextBfAmount &&
          toAmount(claim.wagesEarned) === proratedWages &&
          toAmount(claim.overtimeAmount) === nextOvertime &&
          toAmount(claim.deductionCashAdvanceFromOffice) === nextCrewDeduction
        ) {
          return
        }

        onChange(
          withDerivedValues({
            ...claim,
            bfAmount: nextBfAmount,
            wagesEarned: proratedWages,
            overtimeAmount: nextOvertime,
            deductionCashAdvanceFromOffice: nextCrewDeduction,
            deductionOtherDeductions: 0,
            deductionInternetCard: 0,
            deductionsTotal: nextCrewDeduction,
          })
        )
      } catch (err) {
        console.error('Error hydrating claim details:', err)
      }
    }

    hydrateClaimData()

    return () => {
      isMounted = false
    }
  }, [
    isOpen,
    claim?.crewId,
    claim?.claimMonth,
    claim?.salary,
    signOnOffRecords,
  ])

  if (!isOpen || !claim) return null


  const updateField = (field: keyof CrewClaim, value: string | number | any[]) => {
    const next = withDerivedValues({
      ...claim,
      [field]: value,
    })
    onChange(next)
  }

  const handleCrewChange = async (crewId: number) => {
    const selectedCrew = crewMembers.find((c) => c.id === crewId)
    const crewSignOnMonth = toSignOnMonthKey(selectedCrew?.latestSignOnDate)
    const currentClaimMonth = normalizeMonthStart(claim.claimMonth).slice(0, 7)
    const nextClaimMonth =
      crewSignOnMonth && (!currentClaimMonth || currentClaimMonth < crewSignOnMonth)
        ? `${crewSignOnMonth}-01`
        : claim.claimMonth

    const [latestBanking, allotments, claimsList] = crewId
      ? await Promise.all([
          getCrewLatestBankingDetails(crewId),
          getCrewAllotments(crewId).catch(() => []),
          getCrewClaims().catch(() => []),
        ])
      : [null, [], []]
    const rawSalary = Number(latestBanking?.salary ?? 0)
    const salary = Number.isFinite(rawSalary) ? rawSalary : 0
    const prefetchedBfAmount = resolvePreviousMonthCarryForward(
      allotments,
      nextClaimMonth || claim.claimMonth || new Date().toISOString().slice(0, 7) + '-01',
      claimsList,
      crewId
    )

    onChange({
      ...claim,
      crewId: crewId,
      crewName: selectedCrew?.name || '',
      rankId: selectedCrew?.rankId || null,
      rankName: ranks.find((r) => r.id === selectedCrew?.rankId)?.rank || '',
      vesselId: selectedCrew?.vesselId ?? selectedCrew?.vessel?.id ?? (isOfficeEntry ? claim.vesselId : null),
      vesselName: selectedCrew?.vessel?.fleet_name || selectedCrew?.vesselName || null,
      salary,
      bfAmount: Number.isFinite(prefetchedBfAmount) ? prefetchedBfAmount : 0,
      overtimeAmount: 0,
      claimMonth: nextClaimMonth,
      salaryCurrency: (latestBanking?.salaryCurrency ?? null) as string | null,
      salaryOtherCurrency: latestBanking?.salaryOtherCurrency ?? null,
    })
  }

  const handleRankChange = (rankId: number) => {
    const selectedRank = ranks.find((r) => r.id === rankId)
    onChange({
      ...claim,
      rankId: rankId,
      rankName: selectedRank?.rank || '',
    })
  }

  // Handle file selection for new claims
  const handlePendingFilesChange = (files: FileList | null) => {
    if (!files) return
    const incoming = Array.from(files)
    const validFiles: File[] = []
    for (const f of incoming) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`File "${f.name}" exceeds 10MB limit`)
      } else {
        validFiles.push(f)
      }
    }
    if (validFiles.length > 0) {
      setPendingFiles((prev) => [...prev, ...validFiles])
      setSelectedFileNames(validFiles.map((f) => f.name).join(', '))
    }
  }

  // Remove pending file (for new claims)
  const handleRemovePendingFile = (fileName: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.name !== fileName))
  }

  // Handle save with pending files
  const handleSaveClick = () => {
    if (!allowClaimSave) {
      toast.error('You do not have permission to save this claim.')
      return
    }
    if (isNewClaim) {
      onSave(pendingFiles)
      setPendingFiles([]) // Clear pending files after save
    } else {
      onSave()
    }
  }

  // Handle close and reset pending files
  const handleCloseClick = () => {
    setPendingFiles([])
    setSelectedFileNames('')
    onClose()
  }

  // Can edit only when claim is DRAFT (only for creators)
  const canEdit =
    ((canCreate || canEditDraft) &&
      (claim.status === 'DRAFT' ||
        (canEditMasterPending && claim.status === 'MASTER_REVIEW_PENDING'))) ||
    (canEditSubmittedByOffice && claim.status === 'SUBMITTED')
  const isGeneralEditable = canEdit || isNewClaim
  const isDuplicateMonthLocked = isGeneralEditable && lockClaimAmountInputs
  const isFirstFillMedicalTravelOnly =
    restrictToMedicalTravelOnFirstFill && (isNewClaim || claim.status === 'DRAFT')
  const canEditClaimMonth = isGeneralEditable
  const canEditAllOtherFields =
    isGeneralEditable && !isFirstFillMedicalTravelOnly && !isDuplicateMonthLocked
  // On first-fill for crew, keep non-claim fields restricted but allow claim amount entry.
  const canEditClaimAmountFields = isGeneralEditable && !isDuplicateMonthLocked
  const canEditCrewEnteredCoreFields =
    canEditClaimAmountFields && !lockCrewEnteredCoreFieldsForMaster
  const showRemarksField = !(canEditSubmittedByOffice && claim.status === 'SUBMITTED')
  const canPersistNewClaim = isSuperAdmin || hasPermission('CREATE_CREW_CLAIM')
  const canPersistUpdateClaim = isSuperAdmin || hasPermission('UPDATE_CREW_CLAIM')
  const canPersistOfficeOnSubmitted =
    isSuperAdmin || hasPermission('APPROVE_CREW_CLAIM') || hasPermission('UPDATE_CREW_CLAIM')
  const allowClaimSave =
    (isNewClaim && canPersistNewClaim) ||
    (!isNewClaim &&
      canEdit &&
      (claim.status === 'SUBMITTED' ? canPersistOfficeOnSubmitted : canPersistUpdateClaim))
  const claimCurrency = resolveCurrencyCode(claim.salaryCurrency, claim.salaryOtherCurrency)
  const claimCurrencySymbol = getCurrencySymbol(claimCurrency)
  const deductionsTotal = toAmount(claim.deductionsTotal)
  const crewClaimsAmount =
    toAmount(claim.medical) +
    toAmount(claim.safetyGear) +
    toAmount(claim.travel) +
    toAmount(claim.boardingLodging) +
    toAmount(claim.hraAllowance) +
    toAmount(claim.bonus) +
    toAmount(claim.miscReimbursements) +
    toAmount(claim.overtimeAmount)
  const rawBalanceCf =
    toAmount(claim.bfAmount) + toAmount(claim.wagesEarned) + crewClaimsAmount - deductionsTotal
  const isNegativeBalance = rawBalanceCf < 0
  const negativeBalanceBy = Math.abs(rawBalanceCf)
  const balCf = Math.max(toAmount(claim.balanceCf), 0)

  return (
    <div className='modal-overlay' onClick={handleCloseClick}>
      <style>{`
        .custom-modal-body .form-control:disabled,
        .custom-modal-body .form-control[readonly]:not(.attachment-input),
        .custom-modal-body .form-select:disabled {
          background-color: #f1f5f9 !important;
          color: #64748b !important;
          cursor: not-allowed !important;
        }
      `}</style>
      <div
        className='modal-content'
        onClick={(e) => e.stopPropagation()}
        style={{maxWidth: '1100px', maxHeight: '850px'}}
      >
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>{isNewClaim ? 'Add New Claim' : 'Crew Claim Details'}</h5>
          <button className='close-btn' onClick={handleCloseClick}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className='custom-modal-body' style={{maxHeight: '65vh', overflowY: 'auto'}}>
          <div className='row g-4'>
            {isNewClaim && (isSuperAdmin || isOfficeEntry) && (
              <>
                {showCompanyFilter && <div className='col-md-4'>
                  <label className={LABEL}>Company</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedCompanyGroupId ?? ''}
                    onChange={(event) =>
                      onCompanyGroupChange?.(event.target.value ? Number(event.target.value) : '')
                    }
                    disabled={isDuplicateMonthLocked}
                  >
                    <option value=''>All Companies</option>
                    {(companyAdmins || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>}

                {showSubCompanyFilter && (!selectedCompanyGroupId || filteredCompanyAdmins.length > 0) && (
                  <div className='col-md-4'>
                    <label className={LABEL}>Sub-Company</label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedCompanyAdminId ?? ''}
                      onChange={(event) =>
                        onCompanyAdminChange?.(event.target.value ? Number(event.target.value) : '')
                      }
                      disabled={isDuplicateMonthLocked}
                    >
                      <option value=''>All Sub-Companies</option>
                      {filteredCompanyAdmins.map((ca) => (
                        <option key={ca.id} value={ca.id}>
                          {ca.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                {!isOfficeEntry && <div className='col-md-4'>
                  <label className={LABEL}>Vessel</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedVesselId ?? ''}
                    onChange={(event) =>
                      onVesselChange?.(event.target.value ? Number(event.target.value) : '')
                    }
                    disabled={isDuplicateMonthLocked}
                  >
                    <option value=''>All Vessels</option>
                    {filteredVessels.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.fleet_name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>}
              </>
            )}

            {isOfficeEntry && (
              <div className='col-md-4'>
                <label className={LABEL} htmlFor='claim-entry-vessel'>
                  Vessel <span className='text-danger'>*</span>
                </label>
                <SearchableSelect
                  id='claim-entry-vessel'
                  required
                  className='form-select'
                  value={claim.vesselId || ''}
                  disabled={!isGeneralEditable || isDuplicateMonthLocked || isLoading ||
                    (!isNewClaim && (claim.status !== 'DRAFT' || !!claim.submittedAt))}
                  onChange={(event) => {
                    const vesselId = event.target.value ? Number(event.target.value) : null
                    const vessel = (vessels || []).find((v) => v.id === vesselId)
                    onChange({...claim, vesselId, vesselName: vessel?.fleet_name || null})
                  }}
                >
                  <option value=''>Select Vessel</option>
                  {filteredVessels.map((vessel) => (
                    <option key={vessel.id} value={vessel.id}>{vessel.fleet_name}</option>
                  ))}
                </SearchableSelect>
              </div>
            )}

            {/* Crew Member - Dropdown for new claims */}
            <div className='col-md-4'>
              <label className={LABEL}>
                Crew Member <span className='text-danger'>*</span>
              </label>
              {isNewClaim && !lockCrewSelection ? (
                <SearchableSelect
                  className='form-select'
                  value={claim.crewId || ''}
                  onChange={(event) => handleCrewChange(Number(event.target.value))}
                  disabled={isDuplicateMonthLocked}
                >
                  <option value=''>Select Crew Member</option>
                  {crewMembers.map((crew) => (
                    <option key={crew.id} value={crew.id}>
                      {crew.name}
                    </option>
                  ))}
                </SearchableSelect>
              ) : (
                <input type='text' className='form-control' value={claim.crewName || ''} disabled />
              )}
            </div>

            {/* Rank - Dropdown */}
            <div className='col-md-4'>
              <label className={LABEL}>
                Rank <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={claim.rankName || ''}
                placeholder='Select Crew Member'
                disabled
              />
            </div>

            {/* Status - Only show for existing claims, not for new claims */}
            {!isNewClaim && (
              <div className='col-md-4'>
                <label className={LABEL}>Status</label>
                <input type='text' className='form-control' value={claim.status} disabled />
              </div>
            )}

            {/* Claim Month */}
            <div className={isNewClaim ? 'col-md-4' : 'col-md-4'}>
              <label className={LABEL}>Claim Month</label>
              <input
                type='month'
                className='form-control'
                value={claim.claimMonth ? claim.claimMonth.slice(0, 7) : ''}
                onChange={(event) => {
                  const nextMonth = event.target.value
                  updateField('claimMonth', nextMonth ? `${nextMonth}-01` : '')
                }}
                disabled={!canEditClaimMonth}
              />

            </div>

            {isDuplicateMonthLocked && (
              <div className='col-12'>
                <div className='alert alert-warning py-2 px-3 mb-0'>
                  Claim already exists for this crew and month. Select a different claim month to
                  continue.
                </div>
              </div>
            )}

            {/* Salary */}
            <div className='col-md-4'>
              <label className={LABEL}>Salary</label>
              <div className='input-group'>
                <span className='input-group-text'>{claimCurrencySymbol}</span>
                <input
                  type='number'
                  className='form-control'
                  placeholder='0.00'
                  value={claim.salary ?? 0}
                  min={0}
                  readOnly
                />
              </div>
              <small className='text-muted'>Auto-fetched from crew banking salary.</small>
            </div>

            {showOneTimeClaimFields && (
              <>
                {/* Medical */}
                <div className='col-md-4'>
                  <label className={LABEL}>Medical</label>
                  <div className='input-group'>
                    <span className='input-group-text'>{claimCurrencySymbol}</span>
                    <input
                      type='number'
                      className='form-control'
                      placeholder='0.00'
                      value={claim.medical || ''}
                      onWheel={blurNumberInputOnWheel}
                      onKeyDown={preventNumberInputArrowStep}
                      onChange={(event) => updateField('medical', Number(event.target.value))}
                      min={0}
                      disabled={!canEditCrewEnteredCoreFields}
                    />
                  </div>
                </div>

                {/* Travel */}
                <div className='col-md-4'>
                  <label className={LABEL}>Travel</label>
                  <div className='input-group'>
                    <span className='input-group-text'>{claimCurrencySymbol}</span>
                    <input
                      type='number'
                      className='form-control'
                      placeholder='0.00'
                      value={claim.travel || ''}
                      onWheel={blurNumberInputOnWheel}
                      onKeyDown={preventNumberInputArrowStep}
                      onChange={(event) => updateField('travel', Number(event.target.value))}
                      min={0}
                      disabled={!canEditCrewEnteredCoreFields}
                    />
                  </div>
                </div>

                {/* Boarding & Lodging */}
                <div className='col-md-4'>
                  <label className={LABEL}>Boarding & Lodging</label>
                  <div className='input-group'>
                    <span className='input-group-text'>{claimCurrencySymbol}</span>
                    <input
                      type='number'
                      className='form-control'
                      placeholder='0.00'
                      value={claim.boardingLodging || ''}
                      onWheel={blurNumberInputOnWheel}
                      onKeyDown={preventNumberInputArrowStep}
                      onChange={(event) =>
                        updateField('boardingLodging', Number(event.target.value))
                      }
                      min={0}
                      disabled={!canEditCrewEnteredCoreFields}
                    />
                  </div>
                </div>

                {lockCrewEnteredCoreFieldsForMaster && (
                  <div className='col-12'>
                    <small className='text-muted'>
                      Medical, Travel, and Boarding & Lodging were filled by crew and are locked
                      during master review.
                    </small>
                  </div>
                )}
              </>
            )}

            {/* HRA Allowance */}
            <div className='col-md-4'>
              <label className={LABEL}>HRA Allowance</label>
              <div className='input-group'>
                <span className='input-group-text'>{claimCurrencySymbol}</span>
                <input
                  type='number'
                  className='form-control'
                  placeholder='0.00'
                  value={claim.hraAllowance || ''}
                  onWheel={blurNumberInputOnWheel}
                  onKeyDown={preventNumberInputArrowStep}
                  onChange={(event) => updateField('hraAllowance', Number(event.target.value))}
                  min={0}
                  disabled={!canEditAllOtherFields}
                />
              </div>
            </div>

            {/* Bonus */}
            <div className='col-md-4'>
              <label className={LABEL}>Bonus</label>
              <div className='input-group'>
                <span className='input-group-text'>{claimCurrencySymbol}</span>
                <input
                  type='number'
                  className='form-control'
                  placeholder='0.00'
                  value={claim.bonus || ''}
                  onWheel={blurNumberInputOnWheel}
                  onKeyDown={preventNumberInputArrowStep}
                  onChange={(event) => updateField('bonus', Number(event.target.value))}
                  min={0}
                  disabled={!canEditAllOtherFields}
                />
              </div>
            </div>

            {/* Misc. Reimbursements */}
            <div className='col-md-4'>
              <label className={LABEL}>Misc. Reimbursements</label>
              <div className='input-group'>
                <span className='input-group-text'>{claimCurrencySymbol}</span>
                <input
                  type='number'
                  className='form-control'
                  placeholder='0.00'
                  value={claim.miscReimbursements || ''}
                  onWheel={blurNumberInputOnWheel}
                  onKeyDown={preventNumberInputArrowStep}
                  onChange={(event) =>
                    updateField('miscReimbursements', Number(event.target.value))
                  }
                  min={0}
                  disabled={!canEditAllOtherFields}
                />
              </div>
            </div>

            {showOvertimeAmountField && (
              <div className='col-md-4'>
                <label className={LABEL}>Overtime Amount</label>
                <div className='input-group'>
                  <span className='input-group-text'>{claimCurrencySymbol}</span>
                  <input
                    type='number'
                    className='form-control'
                    value={toAmount(claim.overtimeAmount)}
                    readOnly
                  />
                </div>
                <small className='text-muted'>
                  Auto-fetched from Crew Overtime for selected month.
                </small>
              </div>
            )}

            {/* Total Claim Amount */}
            <div className='col-md-4'>
              <label className={LABEL}>Total Claim Amount</label>
              <div
                className='form-control fw-bold'
                style={{background: '#f0f9ff', color: '#0066cc'}}
              >
                {`${claimCurrencySymbol}${formatCurrency(crewClaimsAmount)}`}
              </div>
            </div>

            <div className='col-12'>
              <div className='border rounded p-3 bg-light'>
                <div className='fw-semibold text-dark mb-2'>
                  B/F Amount + Wages Earned + Crew Claims - Crew Deduction = Balance Wages
                </div>
                <div className='table-responsive border rounded'>
                  <table className='table table-bordered align-middle mb-0'>
                    <thead>
                      <tr>
                        <th className='fw-semibold text-dark bg-light text-nowrap'>B/F Amount</th>
                        <th className='fw-semibold text-dark bg-light text-nowrap'>Wages Earned</th>
                        <th className='fw-semibold text-dark bg-light text-nowrap'>Crew Claims</th>
                        <th className='fw-semibold text-dark bg-light text-nowrap'>
                          Crew Deduction
                        </th>
                        <th className='fw-semibold text-dark bg-light text-nowrap'>
                          Balance Wages
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <input
                            type='number'
                            className='form-control'
                            value={claim.bfAmount ?? 0}
                            readOnly
                            min={0}
                          />
                        </td>
                        <td>
                          <input
                            type='number'
                            className='form-control'
                            value={claim.wagesEarned ?? 0}
                            readOnly
                            min={0}
                          />
                        </td>
                        <td>
                          <input
                            type='number'
                            className='form-control'
                            value={crewClaimsAmount}
                            readOnly
                            min={0}
                          />
                        </td>
                        <td>
                          <input
                            type='number'
                            className='form-control'
                            value={deductionsTotal}
                            readOnly
                            min={0}
                          />
                        </td>
                        <td>
                          <div
                            className='form-control fw-bold'
                            style={{background: '#f0f9ff', color: '#0066cc'}}
                          >
                            {`${claimCurrencySymbol}${formatCurrency(balCf)}`}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {isNegativeBalance && (
                  <div className='alert alert-warning mt-3 mb-0 py-2 px-3'>
                    Balance Wages cannot be negative. This entry is short by {claimCurrencySymbol}
                    {formatCurrency(negativeBalanceBy)}. Reduce Crew Deduction or increase B/F
                    Amount, Wages Earned, or Crew Claims.
                  </div>
                )}
              </div>
            </div>

            {/* Remarks */}
            {showRemarksField && (
              <div className='col-md-6'>
                <label className={LABEL}>Remarks</label>
                <textarea
                  className='form-control'
                  rows={3}
                  placeholder='Enter any remarks or notes...'
                  value={claim.remarks || ''}
                  onChange={(event) => updateField('remarks', event.target.value)}
                  disabled={!(canEditCrewEnteredCoreFields || canEditAllOtherFields)}
                />
              </div>
            )}

            {/* Attachments */}
            <div className={showRemarksField ? 'col-md-6' : 'col-md-12'}>
              <label className={LABEL}>Attachments</label>

              {/* File input - show for new claims or editable existing claims */}
              {(isNewClaim || canEdit) && !isDuplicateMonthLocked && canAddClaimAttachment && (
                <div className='input-group mb-2'>
                  <input
                    type='text'
                    className='form-control attachment-input'
                    placeholder='No file chosen'
                    value={
                      isNewClaim
                        ? pendingFiles.length > 0
                          ? pendingFiles.map((f) => f.name).join(', ')
                          : ''
                        : selectedFileNames || ''
                    }
                    readOnly
                  />
                  <button
                    type='button'
                    className='btn btn_primary'
                    onClick={() => document.getElementById('crew-claim-file-input')?.click()}
                  >
                    Choose Files
                  </button>
                  <input
                    id='crew-claim-file-input'
                    type='file'
                    className='d-none'
                    multiple
                    onChange={(event) => {
                      const files = event.target.files
                      if (isNewClaim) {
                        handlePendingFilesChange(files)
                      } else {
                        if (files && files.length > 0) {
                          const validFiles: File[] = []
                          for (const f of Array.from(files)) {
                            if (f.size > 10 * 1024 * 1024) {
                              toast.error(`File "${f.name}" exceeds 10MB limit`)
                            } else {
                              validFiles.push(f)
                            }
                          }
                          if (validFiles.length > 0) {
                            setSelectedFileNames(
                              validFiles
                                .map((f) => f.name)
                                .join(', ')
                            )
                            onAttachmentAdd(files)
                          }
                        }
                      }
                      // Reset input value to allow selecting same file again
                      event.target.value = ''
                    }}
                  />
                </div>
              )}

              {/* Pending files for new claims */}
              {isNewClaim && pendingFiles.length > 0 && (
                <div className='d-flex flex-wrap gap-2 mb-2'>
                  {pendingFiles.map((file, idx) => (
                    <span
                      key={`${file.name}-${idx}`}
                      className='badge text-dark d-flex align-items-center'
                      style={{background: '#f1f1f2', fontWeight: 500}}
                      title={file.name}
                    >
                      {file.name}
                      <span
                        role='button'
                        tabIndex={0}
                        aria-label={`Remove ${file.name}`}
                        className='ms-2'
                        style={{cursor: 'pointer', color: '#d9214e', fontWeight: 700}}
                        onClick={() => handleRemovePendingFile(file.name)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleRemovePendingFile(file.name)
                          }
                        }}
                      >
                        ×
                      </span>
                    </span>
                  ))}
                </div>
              )}

              {isNewClaim && pendingFiles.length === 0 && (
                <small className='text-muted d-block mb-2'>
                  Select files to attach to this claim.
                </small>
              )}

              {/* Existing attachments for saved claims */}
              {!isNewClaim && (
                <div
                  className='d-flex flex-column gap-1'
                  style={{maxHeight: 120, overflowY: 'auto'}}
                >
                  {!claim.attachments || claim.attachments.length === 0 ? (
                    <span className='text-muted fs-7'>No attachments</span>
                  ) : (
                    claim.attachments.map((file) => (
                      <div
                        key={file.id}
                        className='d-flex align-items-center justify-content-between border rounded px-2 py-1'
                        style={{backgroundColor: '#f8f9fa'}}
                      >
                        <span
                          className='text-truncate fs-7'
                          style={{maxWidth: 150}}
                          title={file.fileName}
                        >
                          {file.fileName}
                        </span>
                        {canEdit && !isDuplicateMonthLocked && canRemoveClaimAttachment && (
                          <button
                            className='btn btn-sm btn-outline-danger'
                            onClick={() => onAttachmentRemove(file.id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='custom-modal-footer d-flex justify-content-end gap-2 border-top px-4 py-3'>
          <button type='button' className='btn btn_secondary' onClick={handleCloseClick}>
            Cancel
          </button>
          {(canEdit || isNewClaim) && allowClaimSave && (
            <button
              type='button'
              className='btn btn_primary'
              onClick={handleSaveClick}
              disabled={isLoading || isNegativeBalance || isDuplicateMonthLocked}
              title={isNegativeBalance ? 'Balance Wages cannot go below 0' : undefined}
            >
              {isLoading ? 'Saving...' : isNewClaim ? 'Create Claim' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default CrewClaimModal
